import React, { useContext, useMemo, useState, useCallback, useImperativeHandle, useEffect } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { ImageOData } from '@bingads-webui-campaign/image-odata';
import { convertFromAdModel, processViewModelDataForSaving } from '@bingads-webui-campaign-react/responsive-ad-editor-utils';
import { withRecommendationContext, RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { withBannerService } from '@bingads-webui/inline-copilot';
import { testUurl, uurlLevels } from '@bingads-webui-component-legacy/upgraded-url/service/testUurl';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { CopilotSearchMMAEditorContainer } from './copilot-search-mma-editor-container';
import { mapBulkUpsertErrors } from './utils/errors-util';
import { useAppliedLog } from './useAppliedLog';

const emptyRecommendationData = {
  Images: {
    ImageList: [],
    ImageAssets: null,
  },
  Headlines: {
    Headline0: '',
    Headline1: '',
    Headline2: '',
  },
  LongHeadlines: {
    LongHeadline0: '',
  },
  Descriptions: {
    Description0: '',
    Description1: '',
  },
};

const emptyData = {
  ...emptyRecommendationData,
  BusinessName: undefined,
  FinalUrls: [],
  MobileFinalUrls: [],
  TrackingUrlTemplate: undefined,
  UrlCustomParameters: undefined,
  FinalUrlSuffix: undefined,
};

const CopilotSearchMMAEditorInternal = ({
  responsiveAdRepository,
  genericImage,
  viewContext,
  ad,
  onChanged,
  perfMarker,
  oDataConfig,
  permissions,
  editorRef,
  newI18n,
  externalErrors,
  showAllValidationErrors,
  showUnmappedFormErrors,
}) => {
  const { scope } = useContext(ScopeContext);
  const { cid, aid, uid } = scope.values || {};
  const i18n = useContext(I18nContext);
  const [data, setData] = useState(() => (ad ? convertFromAdModel(ad) : {}));
  const [isDataValid, setIsDataValid] = useState(false);
  const [errors, setErrors] = useState(ad ? (ad.errors || []) : []);
  const [blocking, setBlocking] = useState(false);
  const imageOData = useMemo(() => new ImageOData(cid, aid, oDataConfig), [cid, aid, oDataConfig]);
  const [saveFailed, setSaveFailed] = useState(false);
  const [, setRecommendationContext] = useContext(RecommendationContext);
  const campaignId = viewContext.currentCampaign && viewContext.currentCampaign.Id;
  const adgroupId = viewContext.currentAdGroup && viewContext.currentAdGroup.Id;
  const { logAppliedSuggestion } = useAppliedLog({ campaignId });
  const activityContext = useContext(ActivityContext);
  const log = activityContext.signal;

  const clearAllErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const onDataChange = useCallback(({ data: newData, isValid }) => {
    setData(newData);
    setIsDataValid(isValid);
    onChanged(newData, isValid);
  }, [onChanged]);

  const onReset = useCallback((clearAll) => {
    const dataPatch = {
      data: {
        ...data,
        ...(clearAll ? emptyData : emptyRecommendationData),
      },
      isValid: false,
    };

    onDataChange(dataPatch);
  }, [data, onDataChange]);

  useImperativeHandle(editorRef, () => ({
    saveAdAsync: () => {
      if (isDataValid) {
        setSaveFailed(false);
        setBlocking(true);
        clearAllErrors();

        const adData = processViewModelDataForSaving(data, campaignId, adgroupId, ad && ad.isSaved !== false && ad.Id);

        return responsiveAdRepository.processImageAndSaveSearchAdAsync(adData, imageOData, false)
          .then((responseData) => {
            if (responseData) {
              const descriptions = JSON.parse(responseData.Descriptions); // empty?
              const headlines = JSON.parse(responseData.Headlines);
              const longHeadlines = JSON.parse(responseData.LongHeadlines);
              logAppliedSuggestion({
                assetData: {
                  Descriptions: _.pluck(descriptions, 'Text'),
                  Headlines: _.pluck(headlines, 'Text'),
                  LongHeadlines: _.pluck(longHeadlines, 'Text'),
                  Images: {
                    ImageList: responseData.ImageList,
                  },
                  FinalUrls: responseData.FinalUrls,
                  Id: responseData.Id,
                },
                isEditing: !!adData.Id,
              });
            }
            return responseData;
          })
          .catch((response) => {
            setSaveFailed(true);
            if (response) {
              const resErrors = _.unique(response.errors, false, err => err.ErrorNumber);
              setErrors(mapBulkUpsertErrors({
                errors: resErrors, i18n, scenario: 'mmaSave',
              }));
              return false;
            }
            return false;
          }).finally(() => {
            setBlocking(false);
          });
      }
      setSaveFailed(true);
      return Promise.resolve(false);
    },
    saveToClient: () => {
      if (isDataValid) {
        clearAllErrors();
        const adData = processViewModelDataForSaving(data, campaignId, adgroupId, ad && ad.isSaved !== false && ad.Id);
        return responsiveAdRepository.processImageAndSaveToClientAsync(adData, imageOData, false);
      }
      setSaveFailed(true);
      return Promise.resolve(false);
    },
    hasCreatedAd: () => {
    },
    onCreateAd: () => {
      setRecommendationContext({
        themes: [],
        headlines: [],
        longHeadlines: [],
        descriptions: [],
        imageAssets: [],
      });
      onReset(true);
    },
    logAppliedSuggestion,
    // for recommendation
    // setResponseErrors: () => {
    // },
    // getData: () => {
    // },
    // getProcessedDataAsync: () => {
    // },
    // isDataValid: () => {
    // },
    // getFlattenedImageAssetList
  }));

  useEffect(() => () => {
    log(`new search mma editor url: ${data.FinalUrls}`);
  }, [data.FinalUrls, log]);

  const triggerTestUurl = useCallback((fn, originalView) => {
    originalView.isInline = false; // eslint-disable-line
    originalView.level = uurlLevels.Ad; // eslint-disable-line
    const params = data || {};
    const context = {
      customerId: +cid,
      userId: +uid,
      accountId: +aid,
      campaignId,
      adGroupId: adgroupId,
      entityId: (ad && ad.Id) || '',
    };
    const options = {
      apiInLog: 'responsive-ad-editor/uurlTest',
    };
    testUurl(fn, originalView, params, context, options);
  }, [ad, adgroupId, aid, cid, data, uid, campaignId]);

  return (
    <UiBlocker
      blocking={blocking}
      loadingText={i18n.getString('Loading')}
    >
      <CopilotSearchMMAEditorContainer
        data={data}
        adId={ad && ad.Id}
        errors={externalErrors || errors}
        onDataChange={onDataChange}
        onReset={onReset}
        perfMarker={perfMarker}
        permissions={permissions}
        viewContext={viewContext}
        genericImage={genericImage}
        showImageOptedIn={false}
        showAllValidationErrors={showAllValidationErrors || saveFailed}
        showUnmappedFormErrors={showUnmappedFormErrors}
        triggerTestUurl={triggerTestUurl}
        newI18n={newI18n}
      />
    </UiBlocker>
  );
};

CopilotSearchMMAEditorInternal.propTypes = {
  responsiveAdRepository: PropTypes.shape({
    processImageAndSaveSearchAdAsync: PropTypes.func.isRequired,
    processImageAndSaveToClientAsync: PropTypes.func.isRequired,
  }).isRequired,
  genericImage: PropTypes.string.isRequired,
  viewContext: PropTypes.shape({
    currentAdGroup: PropTypes.shape({
      Id: PropTypes.number,
    }),
    currentCampaign: PropTypes.shape({
      Id: PropTypes.number,
    }),
  }).isRequired,
  ad: PropTypes.shape({
    Images: PropTypes.shape({}),
    Id: PropTypes.number,
    ImageList: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))),
    errors: PropTypes.arrayOf(PropTypes.shape({})),
    isSaved: PropTypes.bool,
  }),
  onChanged: PropTypes.func,
  perfMarker: PropTypes.shape({
    willFetchFile: PropTypes.func.isRequired,
    willInit: PropTypes.func.isRequired,
    willFetchData: PropTypes.func.isRequired,
    willRender: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
    createChild: PropTypes.func.isRequired,
  }),
  oDataConfig: PropTypes.shape({
    odataRootUrl: PropTypes.string,
    odataPath: PropTypes.string,
    odataToken: PropTypes.string,
  }),
  permissions: PropTypes.shape({
    IsBoostPortraitImageEnabled: PropTypes.bool,
    IsStockPhotoFilterEnabled: PropTypes.bool,
    IsVideoAdsForCampaignEnabled: PropTypes.bool,
    IsMultiMediaAdsPhase2Enabled: PropTypes.bool,
    IsVideoAdEditorEnabled: PropTypes.bool,
    IsAmplifyEnabled: PropTypes.bool,
    IsBoost200x100ImageEnabled: PropTypes.bool,
    IsBoostNewAspectRatioSupportEnabled: PropTypes.bool,
    IsMSANLogoAndCTAForMMAEnabled: PropTypes.bool,
    dynamic: PropTypes.shape({}),
  }),
  // onAdSaved: PropTypes.func,
  editorRef: PropTypes.shape({
    current: PropTypes.shape({}),
  }).isRequired,
  newI18n: PropTypes.shape({}).isRequired,
  externalErrors: PropTypes.arrayOf(PropTypes.shape({})),
  showAllValidationErrors: PropTypes.bool,
  showUnmappedFormErrors: PropTypes.bool,
};

CopilotSearchMMAEditorInternal.defaultProps = {
  ad: null,
  onChanged: _.noop,
  perfMarker: {
    willFetchFile: _.noop,
    willInit: _.noop,
    willFetchData: _.noop,
    willRender: _.noop,
    done: _.noop,
    createChild: _.noop,
  },
  oDataConfig: null,
  permissions: { dynamic: {} },
  externalErrors: undefined,
  showAllValidationErrors: false,
  showUnmappedFormErrors: true,
};

export const CopilotSearchMMAEditor = _.compose(
  withBannerService,
  withRecommendationContext
)(CopilotSearchMMAEditorInternal);
